/**
 * Validation Function Registry
 * Central registry for custom validation functions with metadata
 */

export interface ValidationMeta {
  type: string;
  name: string;
  format: string;
  example: string;
  description: string;
  country?: string;
  category?: string;
}

export interface ValidationResult {
  isValid: boolean;
  message: string;
  details?: Record<string, unknown>;
}

export interface ValidationFunction {
  (input: string): ValidationResult;
}

export interface RegisteredValidation {
  meta: ValidationMeta;
  validate: ValidationFunction;
  testStrings: string[];
}

// Registry to store all validation functions
const validationRegistry: Map<string, RegisteredValidation> = new Map();

/**
 * Register a validation function with metadata
 */
export function registerValidation(
  id: string,
  meta: ValidationMeta,
  validate: ValidationFunction,
  testStrings: string[] = []
): void {
  validationRegistry.set(id, {
    meta,
    validate,
    testStrings,
  });
}

/**
 * Get a registered validation function
 */
export function getValidation(id: string): RegisteredValidation | undefined {
  return validationRegistry.get(id);
}

/**
 * Get all registered validations
 */
export function getAllValidations(): Map<string, RegisteredValidation> {
  return new Map(validationRegistry);
}

/**
 * Get validations by category
 */
export function getValidationsByCategory(
  category: string
): RegisteredValidation[] {
  const validations: RegisteredValidation[] = [];
  for (const [, validation] of validationRegistry) {
    if (validation.meta.category === category) {
      validations.push(validation);
    }
  }
  return validations;
}

/**
 * Get validations by country
 */
export function getValidationsByCountry(
  country: string
): RegisteredValidation[] {
  const validations: RegisteredValidation[] = [];
  for (const [, validation] of validationRegistry) {
    if (validation.meta.country === country) {
      validations.push(validation);
    }
  }
  return validations;
}

/**
 * Validate input using a registered validation function
 */
export function validateWith(id: string, input: string): ValidationResult {
  const validation = validationRegistry.get(id);
  if (!validation) {
    return {
      isValid: false,
      message: `Validation function '${id}' not found`,
    };
  }

  try {
    return validation.validate(input);
  } catch (error) {
    return {
      isValid: false,
      message: `Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Get validation metadata by ID
 */
export function getValidationMeta(id: string): ValidationMeta | undefined {
  const validation = validationRegistry.get(id);
  return validation?.meta;
}

/**
 * Check if a validation function is registered
 */
export function isValidationRegistered(id: string): boolean {
  return validationRegistry.has(id);
}

/**
 * Get list of all validation IDs
 */
export function getValidationIds(): string[] {
  return Array.from(validationRegistry.keys());
}
